import React from "react";
import { Filter, List, Play, Clock, CalendarDays, CheckCircle } from "lucide-react";

export type MeetingStatusFilterType = "all" | "LIVE" | "UPCOMING" | "PLANNED" | "COMPLETED";

interface MeetingFilterProps {
  startDate: string | null;
  endDate: string | null;
  onDateChange: (startDate: string | null, endDate: string | null) => void;
  selectedStatus: MeetingStatusFilterType;
  onStatusChange: (status: MeetingStatusFilterType) => void;
  onInteraction?: () => void;
}

export default function MeetingStatusFilter({ startDate, endDate, onDateChange, selectedStatus, onStatusChange, onInteraction }: MeetingFilterProps) {
  const statusOptions = [
    {
      key: "all" as MeetingStatusFilterType,
      label: "Tüm Toplantılar",
      icon: List,
    },
    {
      key: "LIVE" as MeetingStatusFilterType,
      label: "Canlı Toplantılar",
      icon: Play,
    },
    {
      key: "UPCOMING" as MeetingStatusFilterType,
      label: "Yaklaşan Toplantılar",
      icon: Clock,
    },
    {
      key: "PLANNED" as MeetingStatusFilterType,
      label: "Planlı Toplantılar",
      icon: CalendarDays,
    },
    {
      key: "COMPLETED" as MeetingStatusFilterType,
      label: "Tamamlanan Toplantılar",
      icon: CheckCircle,
    },
  ];

  const fieldClassName =
    "w-full rounded-xl border border-slate-200/90 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-800 shadow-sm transition hover:border-slate-300 hover:bg-white focus:border-blue-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-50";

  return (
    <div className="pr-2 w-52">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-600" />
          <span className="text-sm font-semibold text-gray-800">Filtreler</span>
        </div>
        <div className="flex items-center gap-1" />
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">Toplantı Durumu</label>
          <div className="space-y-1">
            {statusOptions.map((option) => {
              const Icon = option.icon;
              const isSelected = selectedStatus === option.key;

              return (
                <button
                  key={option.key}
                  onClick={(e) => {
                    e.stopPropagation();
                    onStatusChange(option.key);
                    onInteraction?.();
                  }}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors text-left ${
                    isSelected ? "bg-blue-50 text-blue-700" : "text-gray-700 hover:bg-gray-100"
                  }`}>
                  <Icon className={`w-4 h-4 ${isSelected ? "text-blue-600" : "text-gray-500"}`} />
                  <span className="flex-1">{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">Tarih Aralığı</label>

          <div className="space-y-3">
            <div className="relative">
              <label className="block text-xs font-medium text-gray-600 mb-1">Başlangıç Tarihi</label>
              <input
                type="date"
                value={startDate || ""}
                onChange={(e) => {
                  onDateChange(e.target.value || null, endDate);
                  onInteraction?.();
                }}
                className={fieldClassName}
              />
            </div>

            <div className="relative">
              <label className="block text-xs font-medium text-gray-600 mb-1">Bitiş Tarihi</label>
              <input
                type="date"
                value={endDate || ""}
                onChange={(e) => {
                  onDateChange(startDate, e.target.value || null);
                  onInteraction?.();
                }}
                className={fieldClassName}
              />
            </div>

            {(startDate || endDate) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDateChange(null, null);
                  onInteraction?.();
                }}
                className="w-full text-sm text-red-600 hover:text-red-700 hover:bg-red-50 py-2 px-3 rounded-lg transition-colors font-medium border border-red-200 hover:border-red-300">
                Tarihleri Temizle
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
